import { useContext, useState } from 'react';

import { defaultMaxLength, Input, inputStyle } from 'components/design-system/Input';
import { Slideout, SlideoutCommonProps } from 'components/design-system/Slideout';
import { Text } from 'components/design-system/Text';
import { SellerProfileContext } from 'context/sellerProfile';
import { Offer } from 'types';
import { formatPrice, getListingBreakdownPrice } from 'utils/text-helpers';

import { getOfferWarningText } from './helpers/getOfferWarningText';
import { PayoutBottomSheet } from './PayoutBottomSheet';

const warningStyle = { top: '-110%' };
const MIN_OFFER_PRICE = 0.5; // relative value

type CounterOfferModalProps = {
  offer: Offer;
  onClickCounter: (counterPrice: number) => Promise<unknown>;
  isSeller?: boolean;
} & SlideoutCommonProps;

export const CounterOfferModal: React.FC<CounterOfferModalProps> = ({
  offer,
  isOpen,
  closeModal,
  onClickCounter,
  isSeller
}) => {
  const minimalOfferPrice = Math.ceil(offer.listing.listedPriceCents * MIN_OFFER_PRICE);
  const [counterPrice, setCounterPriceLocal] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [warningText, setWarningText] = useState<string | null>(null);

  const priceHistory = [...offer.priceHistory].reverse();
  const mostRecentCounter = priceHistory[priceHistory.length - 1];
  const mostRecentOffer = priceHistory[priceHistory.length - 2];

  const onSubmit = async () => {
    setIsProcessing(true);
    onClickCounter(counterPrice)
      .catch(e => {
        setWarningText(e.description);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  const { sellerProfile, sellerLevels } = useContext(SellerProfileContext);

  const { earnings, commissionCents, sellerFeeCents, minimumTotalFee } = getListingBreakdownPrice(
    {
      priceCents: counterPrice || 0,
      overrideCommissionRateBips: offer.listing.overrideCommissionRateBips,
      overrideSellerFeeCents: offer.listing.overrideSellerFeeCents,
      overrideMinTotalFeeCents: offer.listing.overrideMinTotalFeeCents
    },
    sellerProfile,
    sellerLevels
  );

  const [isPayoutBottomSheetOpen, setIsPayoutBottomSheetOpen] = useState(false);

  const submitButtonProps = {
    loading: isProcessing,
    onClick: onSubmit,
    disabled: !!warningText || !counterPrice,
    title: 'Counter offer',
    className: 'py-3 w-100 mx-auto riforma-regular fs-14px fs-md-16px'
  };

  const onChangeCounterPrice = (value: string) => {
    setCounterPriceLocal(+value.replace(/[^\d-]/g, '') * 100);
    if (warningText) {
      setWarningText(null);
    }
  };

  const onInputBlur = () => {
    const offerWarningText = getOfferWarningText({
      listingPrice: offer.listing.listedPriceCents,
      offerPrice: counterPrice,
      minimalOfferPrice
    });

    if (offerWarningText) {
      setWarningText(offerWarningText);
    }
  };

  const formattedPrice = formatPrice(counterPrice);
  const showWarningText = counterPrice !== 0 && warningText;
  const formattedPriceValue = formattedPrice.slice(1);

  return (
    <Slideout
      bodyClassName='d-flex flex-column'
      title={isSeller ? 'Review offer' : 'Counter Offer'}
      isOpen={isOpen}
      closeModal={closeModal}
      submit={submitButtonProps}
    >
      <div className='d-flex flex-1 flex-column justify-content-between'>
        <div>
          <div className='d-flex align-items-center justify-content-center my-5'>
            <Text
              className='riforma-light opacity-50'
              color={showWarningText ? 'danger' : 'primary'}
              style={inputStyle}
            >
              {formattedPrice[0]}
            </Text>
            <Input
              isResponsive
              autoFocus
              id='counter-offer-price-input'
              value={formattedPriceValue === '0' ? ' ' : formattedPriceValue}
              setValue={onChangeCounterPrice}
              className='text-center border-0 riforma-light bg-light'
              style={inputStyle}
              isError={!!showWarningText}
              onBlur={onInputBlur}
              maxLength={defaultMaxLength}
            />
          </div>
          {showWarningText && (
            <Text color='danger' className='white-space-break text-center mb-1' style={warningStyle}>
              {warningText}
            </Text>
          )}
          {isSeller && (
            <div
              className='text-center riforma-regular mb-1'
              role='button'
              onClick={() => setIsPayoutBottomSheetOpen(true)}
            >
              <span className='opacity-40'>You&apos;ll make </span>
              <span className='text-decoration-underline'>{formatPrice(earnings)}</span>
            </div>
          )}
        </div>
        <div className='d-flex justify-content-between my-3 text-start'>
          <div className='border border-muted bg-lightPrimaryBackground rounded-2 p-3 w-50 me-2'>
            <div className='text-secondary label text-uppercase'>{isSeller ? 'Top Offer' : 'Your Offer'}</div>
            <div className='text-primary riforma-regular pt-1'>
              {isSeller
                ? formatPrice(offer.priceHistory?.[0]?.priceCents as number)
                : formatPrice(mostRecentOffer?.priceCents)}
            </div>
          </div>
          <div className='border border-muted bg-lightPrimaryBackground rounded-2 p-3 w-50 ms-2'>
            <div className='text-secondary label text-uppercase'>{isSeller ? 'Listed price' : 'Seller Counter'}</div>
            <div className='text-primary riforma-regular pt-1'>
              {isSeller ? formatPrice(offer.listing.listedPriceCents) : formatPrice(mostRecentCounter?.priceCents)}
            </div>
          </div>
        </div>
        {isPayoutBottomSheetOpen && (
          <PayoutBottomSheet
            onClose={() => setIsPayoutBottomSheetOpen(false)}
            earnings={earnings}
            commission={commissionCents}
            sellingPrice={counterPrice}
            sellerFee={sellerFeeCents}
            minimalFee={minimumTotalFee}
          />
        )}
      </div>
    </Slideout>
  );
};
